import { createContext, useContext, useState, useCallback } from "react";
import {
  CreateHelpDeskTicket,
  HelpDeskTicketWithDetails,
  UpdateHelpDeskTicket,
} from "../types/help_desk_tickets.types";
import { HelpDeskTicketsAPI } from "../APIs/help_desk_tickets.api";
import { useToast } from "../components/toast";
import { UsersAPI } from "../APIs/users.api";

export type HelpDeskTicketsType = {
  helpDeskTickets: HelpDeskTicketWithDetails[] | null;
  userHDT: HelpDeskTicketWithDetails[] | null;
  isLoading: boolean;
  error: string | null;
  getHelpDeskTickets: () => void;
  createHelpDeskTicket: (data: CreateHelpDeskTicket) => void;
  updateHelpDeskTicket: (id: number, data: UpdateHelpDeskTicket) => void;
  getUserHDTickets: () => void;
};

const HelpDeskTicketsContext = createContext<HelpDeskTicketsType | undefined>(
  undefined
);

export const HelpDeskTicketsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [helpDeskTickets, setHelpDeskTickets] = useState<
    HelpDeskTicketWithDetails[] | null
  >(null);
  const [userHDT, setUserHDT] = useState<HelpDeskTicketWithDetails[] | []>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();

  const getHelpDeskTickets = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await HelpDeskTicketsAPI.getHelpDeskTickets();
      if (response.statusCode === 200) {
        setHelpDeskTickets(response.data);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError("Error al obtener las solicitudes de ayuda");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getUserHDTickets = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await UsersAPI.getUserHDTickets();
      if (response.statusCode === 200) {
        setUserHDT(response.data);
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError("Error al obtener las solicitudes de ayuda");
    } finally {
      setIsLoading(false);
    }
  }, []);
  const createHelpDeskTicket = useCallback(
    async (data: CreateHelpDeskTicket) => {
      setError(null);
      setIsLoading(true);
      try {
        const response = await HelpDeskTicketsAPI.createHelpDeskTicket(data);
        if (response.statusCode === 201) {
          addToast({
            message: "Solicitud enviada",
            type: "success",
            location: "bottom-center",
            duration: 15000,
          });
          getHelpDeskTickets();
        } else {
          addToast({
            message: response.message,
            type: "error",
            location: "bottom-center",
          });
        }
      } catch (error) {
        addToast({
          message: "Error al enviar solicitud, intente nuevamente",
          type: "error",
          location: "bottom-center",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [addToast, getHelpDeskTickets]
  );

  const updateHelpDeskTicket = useCallback(
    async (id: number, data: UpdateHelpDeskTicket) => {
      setError(null);
      setIsLoading(true);
      try {
        const response = await HelpDeskTicketsAPI.updateHelpDeskTicket(
          id,
          data
        );
        if (response.statusCode === 200) {
          addToast({
            message: "¡Estado de la solicitud actualizado!",
            type: "success",
            location: "bottom-center",
          });
          getHelpDeskTickets();
        } else {
          setError(response.message);
        }
      } catch (error) {
        setError("Error al actualizar la solicitud de ayuda");
      } finally {
        setIsLoading(false);
      }
    },
    [addToast, getHelpDeskTickets]
  );

  return (
    <HelpDeskTicketsContext.Provider
      value={{
        helpDeskTickets,
        isLoading,
        getHelpDeskTickets,
        error,
        createHelpDeskTicket,
        updateHelpDeskTicket,
        getUserHDTickets,
        userHDT,
      }}
    >
      {children}
    </HelpDeskTicketsContext.Provider>
  );
};

export const useHelpDeskTickets = () => {
  const context = useContext(HelpDeskTicketsContext);
  if (!context) {
    throw new Error(
      "useHelpDeskTickets must be used within a HelpDeskTicketsProvider"
    );
  }
  return context;
};
